/**
 * Turning notifications on for this device (D-530).
 *
 * **Every gate here is somebody else's**, which is why this is mostly reporting
 * rather than deciding: the browser owns permission, the operating system owns
 * whether a Push API exists at all, and the studio owns whether it will send
 * anything. A screen that guessed at any of the three would show a switch that
 * does nothing.
 */

export type PushConfig = {
  /** Whether this host will send anything at all (D-521). */
  enabled: boolean

  /** Needed to subscribe. Null when the host has no keys. */
  publicKey: string | null
}

/**
 * Why this device cannot be switched on, or `null` if it can.
 *
 * **Named rather than boolean, because the answers need different screens.**
 * "Add to Home Screen first" is an instruction; "your browser cannot do this" is
 * an apology; "you said no" needs to point at browser settings, which is the one
 * thing the page cannot fix for them.
 */
export type PushObstacle =
  | 'unsupported'
  | 'needs-installing'
  | 'blocked'
  | 'not-configured'

/**
 * An iPad, including one pretending to be a Mac.
 *
 * **iPadOS 13+ reports a desktop Safari user agent**, so the touch points are
 * the only reliable way to tell one from a MacBook. Kept separate from
 * {@link isIos} because the two answer different questions: whether push needs
 * installing, and what to call the device in a list.
 */
export function isIpad(): boolean {
  return (
    /iPad/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

/** iOS grants Web Push to an installed app and never to a Safari tab. */
export function isIos(): boolean {
  return /iPhone|iPod/.test(navigator.userAgent) || isIpad()
}

/** Running from the Home Screen rather than in a browser tab. */
export function isInstalled(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    // Safari's own, which predates the standard and is still what iOS sets.
    (navigator as { standalone?: boolean }).standalone === true
  )
}

/**
 * What is in the way, asked in the order a person would hit it.
 *
 * **`pushManager` is the honest test on iOS, not the version number.** Apple
 * exposes it only in an installed app, so its absence *is* the answer — and
 * checking `isInstalled()` first only lets the screen give the useful
 * instruction instead of the useless one.
 */
export function obstacleFor(config: PushConfig | undefined): PushObstacle | null {
  if (!config?.enabled || !config.publicKey) return 'not-configured'

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return isIos() && !isInstalled() ? 'needs-installing' : 'unsupported'
  }

  if (typeof Notification === 'undefined') return 'unsupported'
  if (Notification.permission === 'denied') return 'blocked'

  return null
}

export async function fetchPushConfig(): Promise<PushConfig> {
  const res = await fetch('/api/push/config')
  if (!res.ok) throw new Error(`Could not check notification settings (${res.status})`)
  return res.json()
}

/**
 * Registers the worker, or returns the registration it already has.
 *
 * **Registering is not asking for anything.** No permission prompt, no
 * notification — so it is safe to do on load, which is what makes
 * `pushsubscriptionchange` reachable at all.
 */
export async function registerWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null

  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' })
  } catch {
    // A worker that will not register is a device that cannot receive push. It
    // is not an error the person can act on, and it must not break the app.
    return null
  }
}

/** The current subscription for this browser, if it has one. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null

  const registration = await navigator.serviceWorker.getRegistration('/')
  return (await registration?.pushManager.getSubscription()) ?? null
}

/**
 * Something a person would recognise in a list of their own devices.
 *
 * **Deliberately coarse.** The job is to tell your phone from your laptop, not
 * to identify a device — so it is a browser and a platform and nothing else, no
 * version, no model.
 */
export function describeThisDevice(): string {
  const agent = navigator.userAgent

  const browser =
    /Edg\//.test(agent) ? 'Edge'
    : /OPR\//.test(agent) ? 'Opera'
    : /Firefox\//.test(agent) ? 'Firefox'
    : /Chrome\//.test(agent) ? 'Chrome'
    : /Safari\//.test(agent) ? 'Safari'
    : 'Browser'

  const platform =
    isIpad() ? 'iPad'
    : isIos() ? 'iPhone'
    : /Android/.test(agent) ? 'Android'
    : /Mac OS X/.test(agent) ? 'Mac'
    : /Windows/.test(agent) ? 'Windows'
    : null

  return platform ? `${browser} on ${platform}` : browser
}

/**
 * A VAPID key travels as base64url and `PushManager` wants raw bytes.
 *
 * Exported so it can be tested on its own: it is pure, it is the one piece of
 * this file that can be silently wrong, and a wrong key produces a subscription
 * that is created successfully and never receives anything.
 */
export function decodeKey(base64Url: string): Uint8Array {
  const padded = base64Url.padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), '=')
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))

  return Uint8Array.from(binary, (c) => c.charCodeAt(0))
}

/**
 * Asks the browser, then tells the studio.
 *
 * **Must be called from a click.** Browsers refuse a permission prompt that no
 * gesture asked for, and Chrome permanently blocks a site that tries.
 */
export async function subscribeThisDevice(publicKey: string): Promise<PushSubscription> {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new Error('Notifications were not allowed on this device.')

  const registration = (await registerWorker()) ?? (await navigator.serviceWorker.ready)

  const existing = await registration.pushManager.getSubscription()

  /*
   * **An existing subscription is reused, never re-created.** Subscribing again
   * with a different key throws in Chrome, and unsubscribing first would drop
   * the endpoint the server already knows — which is a device the family would
   * have to switch on again for no reason.
   */
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      // Required by every browser, and true of this app: every push shows a
      // notification. A silent push would be refused.
      userVisibleOnly: true,
      applicationServerKey: decodeKey(publicKey) as BufferSource,
    }))

  await tellTheStudio(subscription)

  return subscription
}

async function tellTheStudio(subscription: PushSubscription): Promise<void> {
  const raw = subscription.toJSON()

  const res = await fetch('/api/push/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: subscription.endpoint,
      p256dh: raw.keys?.p256dh,
      auth: raw.keys?.auth,
      describedAs: describeThisDevice(),
    }),
  })

  if (!res.ok) throw new Error(`Could not turn notifications on (${res.status})`)
}

/**
 * Tells the studio again about the subscription this browser already holds (D-892).
 *
 * **The screen can say "on" while the studio holds nothing**, and that is the
 * one state nothing here could detect. `/account` reads this device from the
 * browser on purpose — the server knows every device on the account, so asking
 * it would report her phone's subscription while she is looking at her laptop
 * — but the browser cannot see whether the studio still has a row. A send
 * refused as `Gone` archives one (D-528), and a reinstalled app or a rotated
 * endpoint leaves the two disagreeing with nothing to notice.
 *
 * **`RememberAsync` is an upsert**, so this revives an archived row or writes a
 * fresh one. `pushsubscriptionchange` is the better signal where it fires and
 * carries the endpoint it replaces, which this cannot — but a browser that
 * missed the event, or never had a worker alive to hear it, is precisely the
 * case that leaves a phone silent with every signal saying it is subscribed.
 * A spare row costs one refused send and is then pruned (D-528) - though a
 * browser that keeps offering an endpoint the push service calls `Gone`
 * revives it on the next page load, so that is one wasted attempt per
 * notification rather than one in total.
 *
 * **Silent when it fails**, like {@link releaseOnSignOut}: a repair nobody
 * asked for must never surface as an error on a screen they were reading for
 * another reason, and the likely failure is the ~75 seconds every endpoint
 * refuses after a deploy (D-33), which the next load fixes by itself.
 */
export async function reassertThisDevice(): Promise<void> {
  try {
    const subscription = await currentSubscription()
    if (subscription) await tellTheStudio(subscription)
  } catch {
    // Deliberately nothing. Nobody asked for this and nobody can act on it.
  }
}

/**
 * Off on this device, and off at the studio.
 *
 * **The studio is told first.** If the browser unsubscribes and the request
 * then fails, the server holds an endpoint that no longer exists and the family
 * has no way to reach it — the switch would read as on, and every push would
 * silently die.
 */
export async function unsubscribeThisDevice(): Promise<void> {
  const subscription = await currentSubscription()
  if (!subscription) return

  const res = await fetch('/api/push/unsubscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  })

  if (!res.ok) throw new Error(`Could not turn notifications off (${res.status})`)

  await subscription.unsubscribe()
}

/**
 * The app-icon number, set or cleared (D-530).
 *
 * **Wrapped because two callers need it and one of them is a sign-out.** The
 * bell keeps it in step while the app is open; signing out has to clear it, and
 * relying on the bell's effect for that does not work — the query is evicted, so
 * the effect sees "no data" rather than "zero" and never runs.
 *
 * Android has no badge to set: its badges derive from tray notifications and the
 * API can only suppress one, so these calls are simply absent there.
 */
export function showBadge(count: number): void {
  try {
    if (count > 0) navigator.setAppBadge?.(count)
    else navigator.clearAppBadge?.()
  } catch {
    // A decoration on a home screen must never break anything.
  }
}

/**
 * Forgets this device on the way out of a session (D-529).
 *
 * **The shared family iPad is the case.** One endpoint may legitimately carry a
 * subscription for two accounts, so without this, mum signs out, dad signs in,
 * and mum's notifications keep arriving on a device she is no longer using —
 * and she cannot switch them off without signing back in on it.
 *
 * Never throws: nothing about signing out may fail because of a notification.
 */
export async function releaseOnSignOut(): Promise<void> {
  // The number goes whatever else happens. It is local and instant, and an icon
  // still showing her unread count after she signed out of a shared iPad is the
  // most visible thing on this list.
  showBadge(0)

  try {
    /*
     * **Raced against a short wait, because `unsubscribe()` contacts the push
     * service.** On a phone with a bad connection this would otherwise put an
     * unbounded network call in front of the sign-out request, and the failure
     * mode is a Sign out button that appears dead.
     *
     * Losing the race costs nothing durable: the server's row is pruned the
     * next time a send is refused (D-528).
     */
    await Promise.race([
      unsubscribeThisDevice(),
      new Promise((resolve) => setTimeout(resolve, 2_000)),
    ])
  } catch {
    // Signing out is more important than tidying up after it.
  }
}

/**
 * One kind of notification a person can say yes or no to (D-532).
 *
 * **The second of two gates.** Tonya decides a kind of message may push at all;
 * this is whether *you* want it. Without it the only honest answer to "this is
 * too noisy" is a deploy.
 */
export type PushCategoryRow = {
  category: string
  name: string
  describes: string
  enabled: boolean

  /**
   * On because nobody has said otherwise, rather than because it was chosen.
   *
   * Worth showing: a panel of ticks says nothing about whether anybody has ever
   * looked at it, and those are different states.
   */
  isDefault: boolean
}

export async function fetchPushCategories(): Promise<PushCategoryRow[]> {
  const res = await fetch('/api/push/categories')
  if (!res.ok) throw new Error(`Could not load your notification settings (${res.status})`)
  return res.json()
}

export async function setPushCategory(category: string, enabled: boolean): Promise<void> {
  const res = await fetch('/api/push/categories', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ category, enabled }),
  })

  if (!res.ok) throw new Error(`Could not save that (${res.status})`)
}
